import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useReducer, useRef, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useLocationHref } from '../../hooks/location';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useHasSupportedVideoCodec } from '../../utils';
import { useTimedFlag } from '../../utils/time';
import { Button } from '../Button';
import { Canvas } from '../Canvas';
import { FullscreenButton } from '../FullscreenButton';
import { Hud } from '../Hud';
import { SpeakerIcon } from '../Icon';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotSupported, FirefoxNotSupported } from '../NotSupportedMessage';
import { Stage } from '../Stage';
import { ViewerPreLive } from '../ViewerPreLive';
import { MobileView, ViewToolbarShareButtons, ViewToolbarShareUrlButton, } from './View';
export function ViewerViewMobile({ streamId }) {
    const [ready, setReady] = useReducer(() => true, false);
    const [isVideoStreaming, setIsVideoStreaming] = useTimedFlag(false, 5000);
    const [isAudioOnMute, setIsAudioOnMute] = useState();
    const canvasControllerRef = useRef(null);
    const { requestWakeLock } = useWakeLock({ enabled: true });
    const navigate = useNavigate();
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    const [hudOrientation, setHudOrientation] = useState();
    const url = useLocationHref();
    const [viewElement, setViewElement] = useState(null);
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !ready }), _jsxs(MobileView, { innerRef: setViewElement, onTouchStart: () => {
                    showHud();
                }, onTouchEnd: () => {
                    // Request wake lock again when user touches the screen as
                    // there is an issue with iOS requiring user interaction to
                    // call the api.
                    // See https://developer.mozilla.org/en-US/docs/Web/Security/User_activation
                    requestWakeLock();
                }, onMouseDown: () => {
                    showHud();
                }, children: [_jsxs(Stage, { children: [_jsx(Canvas, { controllerRef: canvasControllerRef, streamId: streamId, onMuteChange: setIsAudioOnMute, onReady: setReady, onReceivedFrame: () => {
                                    setIsVideoStreaming();
                                }, fit: "contain", onVideoPropertiesChange: ({ preferredOrientation }) => {
                                    setHudOrientation(preferredOrientation);
                                }, followPreferredOrientation: true }), !isVideoStreaming && (_jsx(ViewerPreLive, { roundedCorners: false }))] }), _jsxs(ViewerHud, { visible: isHudVisible, orientation: hudOrientation, children: [_jsx("div", { children: _jsxs(TopButtons, { children: [_jsx("div", { children: _jsx(ViewToolbarShareButtons, { children: _jsx(ViewToolbarShareUrlButton, { url: url }) }) }), _jsx("div", { children: _jsx(FullscreenButton, { element: viewElement }) })] }) }), _jsx("div", {}), _jsxs(Buttons, { children: [_jsx(Button, { type: "button", shape: "pill", padding: "none", kind: isAudioOnMute ? undefined : 'secondary', variant: isAudioOnMute ? undefined : 'dark', icon: _jsx(SpeakerIcon, { muted: isAudioOnMute }), onClick: () => {
                                            if (!isAudioOnMute) {
                                                canvasControllerRef.current?.mute();
                                            }
                                            else {
                                                canvasControllerRef.current?.unmute();
                                            }
                                        } }), _jsx(Button, { type: "button", kind: 'destructive', shape: "pill", onClick: () => {
                                            navigate('/');
                                        }, children: "Start own broadcast" })] })] })] })] }));
}
const TopButtons = styled.div `
    display: flex;
    justify-content: space-between;

    button {
        pointer-events: all;
    }
`;
const Buttons = styled.div `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;

    > * {
        pointer-events: all;
    }
`;
const ViewerHud = styled(Hud) `
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 1.25rem;
    box-sizing: border-box;
`;
